// frontend/src/components/Enroll/EnrollmentConfirmationStep.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import TicketSolidIcon from '../Icons/TicketSolidIcon';
import Tag from '../Tag';
import TimeSolidIcon from '../Icons/TimeSolidIcon';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';


const EnrollmentConfirmationStep = ({ enrollmentData, onComplete, onCancel, event, eventLink, onViewDetails }) => {
  const { t } = useTranslation('eventEnroll');
  const { ticket, vehicle } = enrollmentData;

  // Obtener el icono correspondiente al tipo de vehículo
  const VehicleIconComponent = getVehicleIcon(vehicle?.vehicleType);
  const iconProps = {
    ...getDefaultIconProps(theme.fill.brandMain),
    width: 48,
    height: 48
  };
  // const { eventId } = event;
  const eventId = event.id || event._id;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState('');

  // Función personalizada para manejar el cierre del modal
  const handleClose = () => {
    // Ejecutamos onComplete para finalizar el flujo
    onComplete();
    // Luego ejecutamos onCancel si es necesario
    if (onCancel) onCancel();
  };

  // Calcular el tiempo restante hasta el inicio del evento
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const eventStart = new Date(event.startDate);
      const difference = eventStart - now;

      // // Si la fecha ya pasó, mostrar un mensaje adecuado
      // if (difference <= 0) {
      //   setTimeLeft(t('confirmationStep.eventStarted'));
      //   return;
      // }

      // Calcular días, horas y minutos
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      // Formatear el resultado
      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };

    // Calcular inmediatamente
    calculateTimeLeft();

    // Actualizar cada minuto
    const timer = setInterval(calculateTimeLeft, 60000);

    // Limpiar el intervalo cuando el componente se desmonte
    return () => clearInterval(timer);
  }, [event.startDate, t]);

  // console.log(event)

  // Mostrar mensaje personalizado según el tipo de inscripción
  const confirmationMessage = ticket.approvalRequired
    ? (
      <SuccessMessage>
        <Typography $variant="title-5-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as="h2">
          {/* Tu inscripción se ha realizado. */}
          {t('confirmationStep.enrollmentRequested.header')}
        </Typography>
        <Typography $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" as="p" color={theme.colors.defaultStrong}>
          {/* en breve el organizador te hará saber si aprueba o no tu inscripción. */}
          {t('confirmationStep.enrollmentRequested.description')}
        </Typography>
      </SuccessMessage>
    ) : (
      <SuccessMessage>
        <Typography $variant="title-5-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as="h2">
          {t('confirmationStep.enrollmentConfirmed.header', { userName: user.name })}
        </Typography>
        <Typography $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" as="p" color={theme.colors.defaultWeak}>
          {t('confirmationStep.enrollmentConfirmed.description')}
          {/* Se ha enviado un correo electrónico de confirmación a */}
          <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold" color={theme.colors.defaultStrong}>
            {user.email}
          </Typography>
        </Typography>
      </SuccessMessage>
    )

  return (
    <Modal title={t('confirmationStep.title')} onClose={handleClose} maxWidth="500px">
      <Container>
        {confirmationMessage}

        <TicketPurchased>
          <Ticket>
            <TicketSolidIcon height={20} width={20} fill={theme.colors.defaultStrong} />
            <Typography $variant="body-2-medium">
              {ticket.name}
            </Typography>
          </Ticket>
          {ticket.approvalRequired && (
            <Tag $variant='brandSubtle' $textStyle='caption-medium' $textTransform='capitalize' $letterSpacing='none' $borderRadius='8px'>
              {/* Requiere aprobación */}
              {t('confirmationStep.ticketApprovalRequired')}
            </Tag>
          )}
        </TicketPurchased>

        <Counter>
          <TimeSolidIcon height={20} width={20} fill={theme.colors.defaultStrong} />
          <Typography $variant="body-2-medium" style={{ flexGrow: '1' }}>
            {/* El evento empieza en: */}
            {t('confirmationStep.eventTimeLeft')}
          </Typography>
          <Typography $variant="body-1-semibold" color={theme.colors.brandMain}>
            {timeLeft}
          </Typography>
        </Counter>
        {vehicle && (
          <VehicleEnrolledContainer>
            <VehicleHeader>
              <Typography $variant="body-1-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
                {t('confirmationStep.vehicleEnrolled.header')}
                {/* Vehículo inscrito */}
              </Typography>
              <Typography $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultWeak}>
                {t('confirmationStep.vehicleEnrolled.description')}
                {/* Has indicado que asisitirás que con tu vehiculo: */}
              </Typography>

            </VehicleHeader>
            {/* {console.log(vehicle)} */}
            <VehicleEnrolled>
              {vehicle.image ? (
                <VehicleImage src={vehicle.image} alt={`${vehicle.brand} ${vehicle.model}`} />
              ) : (
                <VehicleIconPlaceholder>
                  <VehicleIconComponent {...iconProps} />
                </VehicleIconPlaceholder>
              )}

              <VehicleData>
                <Typography $variant="body-2-medium">
                  {vehicle.brand}
                </Typography>
                <Typography $variant="body-1-semibold">
                  {vehicle.model}
                  {vehicle.nickname && ` - ${vehicle.nickname}`}
                </Typography>
                <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
                  {vehicle.year}

                </Typography>
              </VehicleData>
            </VehicleEnrolled>
          </VehicleEnrolledContainer>
        )}
        <ModalActions>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(eventLink);
              toast.success('URL copiada al portapapeles.');
            }}
            size="l"
            sizeTablet="l"
            sizeMobile="l"
          >
            {/* Compartir evento */}
            {t('confirmationStep.shareEvent')}
          </Button>
          <Button
            $variant="outline"
            size="l"
            onClick={() => {
              if (onViewDetails) {
                onComplete(); // Cerrar el modal de confirmación
                onViewDetails(); // Abrir el drawer
              } else {
                navigate(`/events/${eventId}/enrollment-details`);
              }
            }}
          >
            {/* Ver mi inscripción */}
            {t('confirmationStep.viewEnrollment')}
          </Button>
        </ModalActions>
      </Container>
    </Modal >
  );
};

EnrollmentConfirmationStep.propTypes = {
  enrollmentData: PropTypes.shape({
    ticket: PropTypes.object.isRequired,
    vehicle: PropTypes.object,
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
  eventLink: PropTypes.string.isRequired,
  onViewDetails: PropTypes.func,
};

export default EnrollmentConfirmationStep;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
`;

const Counter = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
`;

const SuccessMessage = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const TicketPurchased = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};

`;

const Ticket = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const VehicleEnrolled = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: flex-start;
  padding: ${({ theme }) => theme.sizing.xs};
`;

const VehicleHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const VehicleEnrolledContainer = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding-bottom: ${({ theme }) => theme.sizing.sm};
  padding-top: ${({ theme }) => theme.sizing.sm};
  width: 100 %;
`;

const VehicleData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding-bottom: ${({ theme }) => theme.sizing.xs};
  padding-top: ${({ theme }) => theme.sizing.xs};
  width: 100 %;
`;

const VehicleImage = styled.img`
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.radius.xxs};
  height: auto;
  box-shadow: 0 0 1px 0 ${({ theme }) => theme.colors.inverseWeak};
  width: 120px;
  object-fit: cover;
`;

const VehicleIconPlaceholder = styled.div`
  width: 120px;
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.radius.xxs};
  background: ${({ theme }) => theme.fill.inverseWeak};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 1px 0 ${({ theme }) => theme.colors.inverseWeak};
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;